// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/roles/PermissionGroup.jsx

import React, { useEffect, useId, useMemo, useState } from "react";

function permissionLabel(permission) {
  return (
    permission?.permission_label ||
    permission?.label ||
    permission?.name ||
    permission?.permission_key ||
    permission?.key ||
    "Permission"
  );
}

function permissionKey(permission) {
  return permission?.permission_key || permission?.key || "permission.key";
}

function permissionDescription(permission) {
  const description = permission?.description;
  const key = permissionKey(permission);

  if (!description || description === key) return null;
  return description;
}

function matchesSearch(permission, searchTerm) {
  const needle = String(searchTerm || "").trim().toLowerCase();
  if (!needle) return true;

  return [
    permissionLabel(permission),
    permissionKey(permission),
    permissionDescription(permission),
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(needle));
}

function groupLabel(value) {
  return String(value || "General")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export default function PermissionGroup({
  title,
  permissions = [],
  compact = false,
  collapsible = false,
  defaultOpen = false,
  searchTerm = "",
}) {
  const generatedId = useId().replace(/:/g, "");
  const contentId = `permission-group-${generatedId}`;
  const [open, setOpen] = useState(Boolean(defaultOpen));

  const visiblePermissions = useMemo(
    () =>
      permissions.filter((permission) =>
        matchesSearch(permission, searchTerm)
      ),
    [permissions, searchTerm]
  );

  useEffect(() => {
    if (String(searchTerm || "").trim()) {
      setOpen(true);
    }
  }, [searchTerm]);

  if (!visiblePermissions.length) return null;

  const contentVisible = !collapsible || open;
  const groupClassName = [
    "enterprise-admin-permission-group",
    compact ? "compact" : "",
    collapsible ? "is-collapsible" : "",
    open ? "is-open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section className={groupClassName}>
      <button
        type="button"
        className="enterprise-admin-permission-group-header"
        onClick={() => collapsible && setOpen((value) => !value)}
        aria-expanded={contentVisible}
        aria-controls={contentId}
        disabled={!collapsible}
      >
        <span className="enterprise-admin-permission-group-title">
          {groupLabel(title)}
        </span>
        <span className="enterprise-admin-permission-group-meta">
          {visiblePermissions.length}{" "}
          {visiblePermissions.length === 1 ? "permission" : "permissions"}
        </span>
        {collapsible ? (
          <span
            className="enterprise-admin-permission-chevron"
            aria-hidden="true"
          >
            {open ? "⌃" : "⌄"}
          </span>
        ) : null}
      </button>

      {contentVisible ? (
        <div id={contentId} className="enterprise-admin-permission-list">
          {visiblePermissions.map((permission) => {
            const key = permissionKey(permission);
            const description = permissionDescription(permission);

            return (
              <article key={key} className="enterprise-admin-permission-item">
                <strong>{permissionLabel(permission)}</strong>
                {description ? <p>{description}</p> : null}
                <code>{key}</code>
              </article>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
